import clsx from "clsx";
import { Link } from "wouter";
import { basisLabel } from "../../shared/money";
import { FixHere } from "../../web/components/FixHere";
import { Box } from "../../web/components/ui";
import { useMoney } from "../../web/lib/money";
import { type CostDraft, draftPartCost, itemTax } from "./data";
import { duration } from "./describe";
import { type MaterialRow, meta, type PriceListData } from "./meta";
import { costWithLabour, labourRateNet, underMinimum } from "./pricing";
import { type ItemDraft, type ProfitCase, profitCases } from "./itemDrafts";

/*
 * What an item makes per unit, estimated, before tax: the shaded box at the bottom of its Pricing and Costing tabs, laid out as the
 * small sums there were: a line each for its part and its labour (what it's charged, what it costs, what that makes, the markup on its
 * cost), and their total. With a price or cost to update, or its pricing or cost being edited (its window's drafts, on either tab:
 * shown as they'd make it before they're saved), as ServiceM8 has it, then once updated.
 */

/** One line: what it's charged, what it costs (null: can't be told), what that makes, and the markup on its cost. */
interface Line {
    label: string;
    note?: string;
    price: number;
    cost: number | null;
}

/** A case's lines (its part, its labour), and its total. Its labour with no labour cost set: its price only (it can't be costed). */
function linesOf(data: PriceListData, c: ProfitCase): { lines: Line[]; total: Line } {
    const charged = (labourRateNet(data.settings, c.tax) * c.minutes) / 60;
    const { profit } = c;
    const lines: Line[] = [{ label: "Part", price: c.priceNet - charged, cost: profit.partNet }];
    if (c.minutes > 0) lines.push({ label: "Labour", note: duration(c.minutes), price: charged, cost: profit.partOnly ? null : profit.labourNet });
    // No labour cost set: what it makes is the part's alone (its labour as charged can't be costed).
    const total = profit.partOnly ? { label: "Total", note: "part only", price: c.priceNet - charged, cost: profit.partNet } : { label: "Total", price: c.priceNet, cost: profit.partNet + profit.labourNet };
    return { lines, total };
}

/** A figure's cell: on the right, its digits lined up. */
const FIGURE = "py-0.5 pl-4 text-right tabular-nums";

export function ProfitBox(
    props: Readonly<{
        data: PriceListData;
        item: MaterialRow;
        /** Its pricing and cost being edited (its window's drafts): shown as they'd make it. */
        draft: ItemDraft | null;
        costDraft: CostDraft | null;
        /** Bumped by Making too little's Show: it's flashed. */
        fixToken?: number;
    }>,
) {
    const { data, item } = props;
    const fmt = useMoney();
    const { rate } = itemTax(data, item);
    const costTarget = props.costDraft ? costWithLabour(data, item, draftPartCost(data, item, props.costDraft)) : undefined;
    const { now, next, changes } = profitCases(data, item, props.draft, costTarget);
    const title = `Per unit (${basisLabel(false, rate)})`;
    const min = data.settings.minMarkup;
    const money = (n: number | null) => (n === null ? "-" : fmt.money(n));

    // A line's four figures, as shown: its price, cost, what that makes, and the markup on its cost ("-": can't be told).
    const figures = (l: Line) => {
        const made = l.cost === null ? null : l.price - l.cost;
        const markup = l.cost === null || l.cost <= 0 || made === null ? null : (made / l.cost) * 100;
        return [money(l.price), money(l.cost), money(made), markup === null ? "-" : fmt.percent(Math.round(markup))];
    };
    // A case's rows, under its label (two cases: "In ServiceM8", then "Once updated" in the amber of something to update), in one
    // table so every column lines up. Once updated, what's the same as the line above is muted, so what changes stands out (`was`).
    const rows = (c: ProfitCase, label: string | null, was?: ProfitCase) => {
        const caseLabel = label ? (
            <tr key={`${label}:label`}>
                <th colSpan={5} scope="colgroup" className={clsx("pb-0.5 text-left text-xs font-normal", was ? "pt-4 font-medium text-warning-ink" : "pt-2 text-muted")}>
                    {label}
                </th>
            </tr>
        ) : null;
        // No cost to tell it from (as ServiceM8 has it, while a draft gives one): said, not shown as a part costing nothing.
        if (c.profit.profitNet === null) {
            return [
                caseLabel,
                <tr key={`${label}:none`}>
                    <td colSpan={5} className="py-0.5 text-muted">
                        No cost
                    </td>
                </tr>,
            ];
        }
        const { lines, total: sum } = linesOf(data, c);
        const before = was && was.profit.profitNet !== null ? linesOf(data, was) : null;
        const low = underMinimum(c.profit.markupPct, min);
        // Under the minimum markup: said briefly beside "Total" (red, as the line is), with anything already noted there.
        const made = c.profit.profitNet;
        let short = "";
        if (low) short = min > 0 ? `under ${fmt.percent(min)} min` : made < 0 ? "a loss" : "makes nothing";
        const total = { ...sum, note: [sum.note, short].filter(Boolean).join(" · ") || undefined };
        const row = (l: Line, isTotal: boolean) => {
            const shown = figures(l);
            // The same line as it is now (none: a new one, e.g. labour just added: all of it changes).
            const same = before ? (isTotal ? before.total : before.lines.find((x) => x.label === l.label)) : undefined;
            const old = same ? figures(same) : null;
            // Red: the total under the minimum markup (it's the whole item's), or any line making a loss (one line can hide in the total).
            const danger = isTotal ? low : l.cost !== null && l.price - l.cost < -0.005;
            const cell = clsx(isTotal && "border-t border-line pt-1");
            return (
                <tr key={`${label}:${l.label}`} className={clsx(isTotal && "font-semibold", danger && "text-danger")}>
                    <th scope="row" className={clsx(cell, "py-0.5 text-left font-[inherit]")}>
                        {l.label}
                        {l.note ? <span className={clsx("ml-1.5 text-xs font-normal", !danger && "text-muted")}>{l.note}</span> : null}
                    </th>
                    {shown.map((f, i) => (
                        <td key={i} className={clsx(FIGURE, cell, old && old[i] === f && !danger && "text-muted")}>
                            {f}
                        </td>
                    ))}
                </tr>
            );
        };
        return [caseLabel, ...lines.map((l) => row(l, false)), row(total, true)];
    };

    return (
        <Box shaded className="mt-auto">
            <FixHere token={props.fixToken} className="-mx-2">
                <div className="space-y-3 rounded-md px-2 py-1">
                    {next.profit.profitNet === null ? (
                        <>
                            <h3 className="box-title m-0">{title}</h3>
                            <p className="m-0 text-sm text-muted">No cost entered yet.</p>
                        </>
                    ) : (
                        // One table (its title on its column headings' line), so every column lines up whatever's in it.
                        <table className="w-full border-collapse">
                            <thead>
                                <tr>
                                    <th scope="col" className="box-title text-left">
                                        {title}
                                    </th>
                                    {["Price", "Cost", "Profit", "Markup"].map((h) => (
                                        <th key={h} scope="col" className={clsx(FIGURE, "text-xs font-normal text-muted")}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>{changes ? [...rows(now, "In ServiceM8"), ...rows(next, "Once updated", now)] : rows(now, null)}</tbody>
                        </table>
                    )}
                    {next.profit.profitNet !== null && next.profit.partOnly ? (
                        <p className="m-0 text-xs text-muted">
                            Its labour isn&apos;t costed:{" "}
                            <Link href={`/t/${meta.id}/settings#labour`} className="whitespace-nowrap">
                                set a labour cost
                            </Link>{" "}
                            to count it.
                        </p>
                    ) : null}
                </div>
            </FixHere>
        </Box>
    );
}
